// frontend/src/components/staff/RecentAutoClockOuts.js
//
// Workforce — TIME-007 §4 Recent Auto-Clock-Outs tab body.
//
// Renders as the body of the new "Recent Auto-Clock-Outs (N)" tab on the
// existing TIME-009 Daily Review dashboard. Surfaces forensic events from
// `GET /api/workforce/auto-clock-out/recent` (Marcus tech doc §5.3 + Simone
// PR #71). Polling cadence: 30s, matching the dashboard's existing pattern.
//
// Per Zuri spec §4.2 — table columns Staff / Original punch-in / Auto-closed
// at / Threshold / Mode / Action / Pay period; controls cluster Date range
// + Behavior mode filter + Refresh button.
//
// Backend contract per Marcus §5.3 captured-curl:
//   {
//     autoClockOutEvents: [
//       { _id, staffProfileId, userId, staffName, studioLocationId,
//         studioLocationCode, punchInTime, punchOutTime, rawTotalMinutes,
//         roundedTotalMinutes, thresholdMinutesAtClose, behaviorAtClose,
//         system_clocked_out, flags, status, implicitBreakClosed,
//         sweepRunId, closedAt }
//     ],
//     count, truncated, windowDays, windowFrom
//   }

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { autoClockOutAPI } from "../../services/api";
import "./RecentAutoClockOuts.css";

const STUDIO_TZ = "America/Phoenix";
const POLL_INTERVAL_MS = 30_000;

const formatStudioDateTime = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return `${m.format("ddd MMM D, h:mm A")} MST`;
};

const formatThreshold = (minutes) => {
  if (typeof minutes !== "number" || Number.isNaN(minutes)) return "—";
  const hours = Math.round(minutes / 60);
  return `${hours} h`;
};

const RecentAutoClockOuts = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [days, setDays] = useState(7);
  const [modeFilter, setModeFilter] = useState("all");
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await autoClockOutAPI.getRecent({ days });
      const data = res?.data || {};
      const list = Array.isArray(data.autoClockOutEvents)
        ? data.autoClockOutEvents
        : [];
      setEvents(list);
      setLastRefresh(new Date());
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchEvents();
    intervalRef.current = setInterval(fetchEvents, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    if (modeFilter === "all") return events;
    if (modeFilter === "auto_closed") {
      return events.filter((e) => e.system_clocked_out === true);
    }
    if (modeFilter === "flagged_only") {
      return events.filter(
        (e) =>
          (Array.isArray(e.flags) && e.flags.includes("flagged_overlong")) ||
          e.flagged_overlong === true
      );
    }
    return events;
  }, [events, modeFilter]);

  return (
    <div className="raco-container" data-testid="raco-container">
      <div className="raco-controls">
        <label className="raco-control">
          <span className="raco-control-label">Range:</span>
          <select
            className="raco-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="raco-range"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </label>

        <fieldset className="raco-mode-filter">
          <legend className="raco-control-label">Mode:</legend>
          <label className="raco-radio">
            <input
              type="radio"
              name="raco-mode"
              value="all"
              checked={modeFilter === "all"}
              onChange={() => setModeFilter("all")}
            />
            All
          </label>
          <label className="raco-radio">
            <input
              type="radio"
              name="raco-mode"
              value="auto_closed"
              checked={modeFilter === "auto_closed"}
              onChange={() => setModeFilter("auto_closed")}
            />
            Auto-closed
          </label>
          <label className="raco-radio">
            <input
              type="radio"
              name="raco-mode"
              value="flagged_only"
              checked={modeFilter === "flagged_only"}
              onChange={() => setModeFilter("flagged_only")}
            />
            Flagged only
          </label>
        </fieldset>

        <button
          type="button"
          className="raco-refresh"
          onClick={fetchEvents}
          data-testid="raco-refresh"
        >
          Refresh
        </button>

        <span className="raco-last-refresh">
          {loading
            ? "Loading..."
            : lastRefresh
              ? `Last refreshed: ${moment.tz(lastRefresh, STUDIO_TZ).format("h:mm:ss A")}`
              : ""}
        </span>
      </div>

      {error ? (
        <div className="raco-error" role="alert" data-testid="raco-error">
          Couldn&apos;t load Auto-Clock-Out events.{" "}
          <button
            type="button"
            className="raco-link"
            onClick={fetchEvents}
            data-testid="raco-retry"
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="raco-loading" role="status" data-testid="raco-loading">
          Loading auto-clock-out events...
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="raco-empty" role="status" data-testid="raco-empty">
          {modeFilter === "flagged_only" ? (
            <>
              <p className="raco-empty-headline">
                No flagged-only events in this range.
              </p>
              <p className="raco-empty-detail">
                Switch behavior to <code>auto_close</code> to bound hours
                automatically.
              </p>
            </>
          ) : (
            <>
              <p className="raco-empty-headline">
                No auto-clock-out events in this range.
              </p>
              <p className="raco-empty-detail">
                Your team is good at remembering to clock out.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="raco-table-wrap">
          <table className="raco-table" data-testid="raco-table">
            <thead>
              <tr>
                <th scope="col">Staff</th>
                <th scope="col">Original punch-in</th>
                <th scope="col">Auto-closed at</th>
                <th scope="col">Threshold</th>
                <th scope="col">Mode</th>
                <th scope="col">Pay period</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((evt) => {
                const flagged =
                  (Array.isArray(evt.flags) &&
                    evt.flags.includes("flagged_overlong")) ||
                  evt.flagged_overlong === true;
                const autoClosed = evt.system_clocked_out === true;
                const mode = autoClosed
                  ? { label: "Auto-closed", className: "raco-mode-auto-closed" }
                  : flagged
                    ? { label: "Flagged", className: "raco-mode-flagged" }
                    : { label: "—", className: "raco-mode-unknown" };
                return (
                  <tr key={evt._id} className="raco-row">
                    <td>
                      <div className="raco-staff">
                        <span className="raco-staff-name">
                          {evt.staffName || "Unknown staff"}
                        </span>
                        {evt.studioLocationCode && (
                          <span className="raco-staff-loc">
                            {evt.studioLocationCode}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="raco-mono">
                      {formatStudioDateTime(evt.punchInTime)}
                    </td>
                    <td className="raco-mono">
                      {autoClosed
                        ? formatStudioDateTime(evt.punchOutTime || evt.closedAt)
                        : "—"}
                    </td>
                    <td>{formatThreshold(evt.thresholdMinutesAtClose)}</td>
                    <td>
                      <span className={`raco-pill ${mode.className}`}>
                        <span aria-hidden="true" className="raco-pill-dot" />
                        {mode.label}
                      </span>
                    </td>
                    <td>
                      {evt.payPeriodLabel ? (
                        <span className="raco-period">{evt.payPeriodLabel}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <a
                        href={`/staff/payroll/timesheets#timelog-${evt._id}`}
                        className="raco-action-link"
                      >
                        {flagged && !autoClosed ? "Resolve →" : "Review →"}
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecentAutoClockOuts;
